<script lang="ts" setup>
import Quill from 'quill'
import { v4 as uuidv4 } from 'uuid'
import { onMounted, onUnmounted, ref, watch } from 'vue'

let quill: Quill | null = null
const id = uuidv4()
const container = ref()
const isEditorFocus = ref<boolean>()

const props = defineProps<{
  placeholder: string
}>()
const emits = defineEmits<{
  focus: []
  'update:modelValue': [modelValue: string]
}>()
const content = defineModel<string>('modelValue', { default: '' })

const handleSelectionChange = () => {
  isEditorFocus.value = !!quill?.hasFocus()
}

watch(isEditorFocus, (focus) => {
  if (focus) {
    emits('focus')
  }
})
watch(content, (value) => {
  if (quill && quill?.getSemanticHTML() !== value) {
    if (value) {
      quill.setContents(
        quill.clipboard.convert({
          html: content.value,
        }),
      )
    } else {
      quill.deleteText(0, quill.getLength(), 'silent')
    }
  }
})

onMounted(() => {
  if (container.value) {
    quill = new Quill(`#editor-${id}`, {
      bounds: '.rich-editor__container',
      modules: {
        toolbar: ['bold', 'italic', 'underline', 'link', { list: 'ordered' }, { list: 'bullet' }],
      },
      placeholder: props.placeholder,
      theme: 'snow',
    })

    quill.setContents(
      quill.clipboard.convert({
        html: content.value,
      }),
    )
    quill.on('text-change', () => emits('update:modelValue', quill?.getSemanticHTML() ?? ''))
    quill.on('selection-change', handleSelectionChange)
  }
})
onUnmounted(() => (quill = null))
</script>
<template>
  <div class="rich-editor__container">
    <div :id="`editor-${id}`" ref="container"></div>
  </div>
</template>
<style lang="scss" scoped>
.rich-editor__container {
  width: 100%;
  border-radius: 8px;
  box-shadow: 0 0 0 1px #dcdfe6 inset;

  &:focus,
  &:hover {
    box-shadow: 0 0 0 1px #c0c4cc inset;
  }
}
:deep(.ql-container) {
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
}

:deep(.ql-toolbar.ql-snow) {
  border: none;
  border-bottom: 1px solid #dcdfe6;
}
:deep(.ql-container.ql-snow) {
  border: none;
}
:deep(.ql-editor) {
  padding: 15px 16px;
}
</style>
